import {useCallback, useEffect, useMemo, useState} from "react";
import {useDebouncer} from "@tanstack/react-pacer";
import {useTranslation} from "react-i18next";
import {SxProps, Theme, alpha, useTheme} from "@mui/material/styles";
import {localeDe, localeEn, localeIt} from "@mobiscroll/react";
import {
    formatFullDMY,
    parseStoredDate,
    serializeStoredDate,
    startOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import {
    DATE_RANGE_FROM_ACROSS_TABS_KEY,
    DATE_RANGE_TO_ACROSS_TABS_KEY,
} from "crqlar-react-common/dist/utils/localStorage/localStorageConstants";
import {usePhoneLayout} from "../../../utils/responsive.util";

/**
 * Shared, non-visual plumbing for {@link SingleDatePicker} and {@link RangeDatePicker}.
 *
 * Everything here is intentionally invisible-infrastructure — locale resolution, popup theming,
 * the mobile breakpoint, date coercion. The *layout and spacing* live in each picker so single
 * and range can be tweaked independently; nothing in this file should need per-variant tuning.
 */

export interface DateRange {
    start: Date;
    end: Date;
}

export type DatePickerChangeReason = "selection" | "step" | "today";

interface PersistentDateRangeState {
    persistenceKey?: string;
    range: DateRange;
}

const createTodayRange = (): DateRange => {
    const today = startOfDay(new Date());
    return {start: today, end: today};
};

const isValidDate = (date: Date | undefined): date is Date => (
    Boolean(date) && !Number.isNaN(date?.getTime())
);

const normalizeRange = (range: DateRange): DateRange => {
    const fallback = createTodayRange();
    const start = isValidDate(range.start) ? startOfDay(range.start) : fallback.start;
    const requestedEnd = isValidDate(range.end) ? startOfDay(range.end) : start;

    return {
        start,
        end: requestedEnd.getTime() >= start.getTime() ? requestedEnd : start,
    };
};

const persistenceStorageKeys = (persistenceKey: string) => ({
    from: `${DATE_RANGE_FROM_ACROSS_TABS_KEY}-${persistenceKey}`,
    to: `${DATE_RANGE_TO_ACROSS_TABS_KEY}-${persistenceKey}`,
});

const readPersistentDateRange = (persistenceKey?: string): DateRange => {
    if (!persistenceKey || typeof window === "undefined") return createTodayRange();

    try {
        const keys = persistenceStorageKeys(persistenceKey);
        const storedStart = parseStoredDate(window.localStorage.getItem(keys.from));
        const storedEnd = parseStoredDate(window.localStorage.getItem(keys.to));
        const start = isValidDate(storedStart)
            ? storedStart
            : isValidDate(storedEnd) ? storedEnd : createTodayRange().start;
        const end = isValidDate(storedEnd) ? storedEnd : start;

        return normalizeRange({start, end});
    } catch {
        return createTodayRange();
    }
};

export const persistDateRange = (
    persistenceKey: string | undefined,
    range: DateRange,
    reason: DatePickerChangeReason = "selection",
) => {
    if (!persistenceKey || typeof window === "undefined") return;

    try {
        const normalizedRange = normalizeRange(range);
        const keys = persistenceStorageKeys(persistenceKey);
        const storageMode = reason === "today" ? "today" : "fixed";
        window.localStorage.setItem(keys.from, serializeStoredDate(normalizedRange.start, storageMode));
        window.localStorage.setItem(keys.to, serializeStoredDate(normalizedRange.end, storageMode));
    } catch {
        // Persistence is optional; the controlled date state remains usable without storage.
    }
};

/**
 * Controlled range state with optional sector-scoped persistence.
 * Callers that pass the same key share the existing cross-tab date-range storage contract.
 * Without a key this behaves exactly like local component state initialized to today.
 */
export const usePersistentDateRange = (persistenceKey?: string) => {
    const [state, setState] = useState<PersistentDateRangeState>(() => ({
        persistenceKey,
        range: readPersistentDateRange(persistenceKey),
    }));
    const range = state.persistenceKey === persistenceKey
        ? state.range
        : readPersistentDateRange(persistenceKey);

    useEffect(() => {
        setState(current => current.persistenceKey === persistenceKey
            ? current
            : {
                persistenceKey,
                range: readPersistentDateRange(persistenceKey),
            });
    }, [persistenceKey]);

    const setRange = useCallback((nextRange: DateRange, reason: DatePickerChangeReason = "selection") => {
        const normalizedRange = normalizeRange(nextRange);
        persistDateRange(persistenceKey, normalizedRange, reason);
        setState({persistenceKey, range: normalizedRange});
    }, [persistenceKey]);

    return [range, setRange] as const;
};

/** Single-date adapter over the same persisted range. Changing it stores a one-day range. */
export const usePersistentSingleDate = (persistenceKey?: string) => {
    const [range, setRange] = usePersistentDateRange(persistenceKey);
    const setDate = useCallback((date: Date, reason: DatePickerChangeReason = "selection") => {
        setRange({start: date, end: date}, reason);
    }, [setRange]);

    return [range.start, setDate] as const;
};

// Shorter than the table search field's 500ms: these are discrete clicks, not keystrokes, so one
// click should start loading almost at once while a burst of them still collapses into one request.
const DATE_QUERY_DEBOUNCE_MS = 300;

/**
 * Persisted range split in two, for screens whose date range drives a server query.
 *
 * `range` updates immediately — bind the picker to it, so the input never waits on the network.
 * `queryRange` trails it by `wait` ms — put *that* in query keys and request bodies, so holding down
 * a step arrow (or picking a range end) costs one request instead of one per click. Pass
 * `onRangeChange` straight to the picker's `onChange`; it feeds both.
 *
 * Anything derived from the fetched rows (a reference date for age categories, say) should follow
 * `queryRange` too, so the data and the values computed against it move in the same step.
 */
export const useDebouncedDateRange = (
    persistenceKey?: string,
    wait: number = DATE_QUERY_DEBOUNCE_MS,
) => {
    const [range, setRange] = usePersistentDateRange(persistenceKey);
    // Stamped with the key it was committed under: a key switch brings in that key's own persisted
    // range, which has to apply at once rather than wait for a debounce only a click would fire.
    const [committed, setCommitted] = useState({persistenceKey, range});
    const queryRange = committed.persistenceKey === persistenceKey ? committed.range : range;
    const {maybeExecute} = useDebouncer(
        (nextRange: DateRange) => setCommitted({persistenceKey, range: normalizeRange(nextRange)}),
        {wait} as Parameters<typeof useDebouncer>[1],
    );

    const onRangeChange = useCallback((
        nextRange: DateRange,
        reason: DatePickerChangeReason = "selection",
    ) => {
        setRange(nextRange, reason);
        maybeExecute(nextRange);
    }, [maybeExecute, setRange]);

    return {range, queryRange, onRangeChange};
};

/**
 * Single-date counterpart of {@link useDebouncedDateRange}, for screens whose selected day drives a
 * server query. Same split: bind the picker to `date`, query on `queryDate`, pass `onDateChange` to
 * the picker's `onChange`.
 *
 * Leading-edge, unlike the range hook: one day-step is already a complete, wanted value, so it fires
 * at once and only a burst collapses — stepping five days fetches the first and the last, never the
 * three you skimmed past. The range hook stays trailing-only because click one of a two-click range
 * pick commits a collapsed single-day range nobody asked for, and firing that would waste a request.
 */
export const useDebouncedSingleDate = (
    persistenceKey?: string,
    wait: number = DATE_QUERY_DEBOUNCE_MS,
) => {
    const [date, setDate] = usePersistentSingleDate(persistenceKey);
    // Stamped with the key it was committed under, for the same reason as the range hook: a key
    // switch brings in that key's own persisted day and has to apply without waiting for a click.
    const [committed, setCommitted] = useState({persistenceKey, date});
    const queryDate = committed.persistenceKey === persistenceKey ? committed.date : date;
    const {maybeExecute} = useDebouncer(
        (nextDate: Date) => setCommitted({persistenceKey, date: startOfDay(nextDate)}),
        {wait, leading: true} as Parameters<typeof useDebouncer>[1],
    );

    const onDateChange = useCallback((
        nextDate: Date,
        reason: DatePickerChangeReason = "selection",
    ) => {
        setDate(nextDate, reason);
        maybeExecute(nextDate);
    }, [maybeExecute, setDate]);

    return {date, queryDate, onDateChange};
};

export type StepButtonsPlacement = "auto" | "inside" | "outside";

// Fixed slot for the weekday prefix so the date column always starts at the same x and the
// trigger width never shifts when navigating to a wider weekday (e.g. "We" vs "Fr").
export const WEEKDAY_SLOT = "1.75em";

// Fixed slot for the DD.MM.YYYY date text. League Spartan (the app font) has no tabular-figure
// feature, so `font-variant-numeric: tabular-nums` is a no-op and its "1" is ~half the width of
// other digits — a single date can swing ~30px depending on which digits show. With the trigger
// sized to `max-content`, that reflows the whole field on every date change. Pinning the date to
// its widest rendering freezes the width; digits containing "1" simply leave trailing space,
// exactly like the weekday slot above. Widest possible date "00.00.0000" measures 9.05ch in
// League Spartan ("0" is the widest digit); 9.25ch leaves a small rounding cushion.
export const DATE_SLOT = "9.25ch";

// Compact slot for the mobile "DD.MM.YY" (2-digit year) format. Widest "00.00.00" ≈ 7.04ch;
// 7.15ch adds the same small rounding cushion as DATE_SLOT.
export const DATE_SLOT_COMPACT = "7.15ch";

/** "DD.MM.YY" — the mobile-compact date format, derived from formatFullDMY ("DD.MM.YYYY"). */
export const formatCompactDMY = (date: Date): string => formatFullDMY(date).replace(/\d{2}(\d{2})$/, "$1");

/**
 * Two trigger sizes, mirroring MUI's input sizing: "medium" (40px, the default, matches a standard
 * input) and "small" (36px). Every size-dependent dimension lives here so both pickers scale as a
 * unit — height, text, the today button, and the step buttons all shrink together rather than just
 * the outer box. The weekday/date slots are in em/ch, so they track fontSize automatically.
 */
export type DatePickerSize = "medium" | "small";

export interface DatePickerSizeTokens {
    height: number; // trigger shell height (px)
    fontSize: number; // date text (px)
    lineHeight: string;
    textNudge: string; // small mt to optically centre League Spartan in the shell
    todayButton: number; // today icon-button box (px)
    todayIcon: number; // calendar glyph (px)
    stepOutside: number; // free-standing step button box (px) — keep equal to height so the arrows
                         // flanking the field are exactly as tall as it is
    stepInsideWidth: number; // tucked-in step button width (px)
    stepIcon: number; // chevron glyph (px)
}

export const DATE_PICKER_SIZE_TOKENS: Record<DatePickerSize, DatePickerSizeTokens> = {
    medium: {
        height: 40,
        fontSize: 14,
        lineHeight: "18px",
        textNudge: "2px",
        todayButton: 34,
        todayIcon: 18,
        stepOutside: 40,
        stepInsideWidth: 34,
        stepIcon: 20,
    },
    small: {
        height: 36,
        fontSize: 13,
        lineHeight: "16px",
        textNudge: "1px",
        todayButton: 30,
        todayIcon: 16,
        stepOutside: 36,
        stepInsideWidth: 30,
        stepIcon: 18,
    },
};

const THEME_STYLE_ELEMENT_ID = "crqlar-new-date-picker-theme-style";
export const NEW_DATE_PICKER_POPUP_CLASS = "crqlar-new-date-picker-popup";

const CALENDAR_LOCALE_BY_LANGUAGE = {
    de: localeDe,
    en: localeEn,
    it: localeIt,
} as const;

/** Resolves the active i18n language plus the matching Mobiscroll locale bundle. */
export const useCalendarLocale = () => {
    const {i18n} = useTranslation();
    const language = i18n.language?.split("-")[0] ?? "en";
    const calendarLocale = useMemo(
        () =>
            CALENDAR_LOCALE_BY_LANGUAGE[language as keyof typeof CALENDAR_LOCALE_BY_LANGUAGE] ??
            localeEn,
        [language],
    );
    return {language, calendarLocale};
};

/**
 * Aligns the portalled Mobiscroll popup with the active MUI theme. One shared global <style> tag
 * (deduped by id) targets the popup's shared cssClass, keeping the overrides local to these new
 * date pickers. Returns the accent colour so callers can reuse it (e.g. the focus ring).
 */
export const useMobiscrollThemeStyles = (): string => {
    const theme = useTheme();
    const accent = theme.palette.primary.main;
    const accentContrast = theme.palette.primary.contrastText;
    const fontFamily = theme.typography.fontFamily;

    useEffect(() => {
        let styleEl = document.getElementById(THEME_STYLE_ELEMENT_ID) as HTMLStyleElement | null;
        if (!styleEl) {
            styleEl = document.createElement("style");
            styleEl.id = THEME_STYLE_ELEMENT_ID;
            document.head.appendChild(styleEl);
        }
        styleEl.textContent = `
            .${NEW_DATE_PICKER_POPUP_CLASS},
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-font {
                font-family: ${fontFamily} !important;
            }
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-selected .mbsc-calendar-cell-text {
                background-color: ${accent} !important;
                border-color: ${accent} !important;
                color: ${accentContrast} !important;
            }
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-calendar-today {
                color: ${accent} !important;
            }
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-calendar-today.mbsc-calendar-cell-text {
                border-color: ${accent} !important;
            }
            /* Mobiscroll marks today with the day circle's own border-colour, so selecting today
               fills that same circle in the accent and today's marker vanishes. Restate it outside
               the circle instead: a detached ring, with a transparent gap so both read at once. The
               ring is a pseudo-element rather than a box-shadow so the gap shows whatever is behind
               it instead of assuming the popup's background colour. */
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-selected .mbsc-calendar-cell-text.mbsc-calendar-today {
                position: relative;
            }
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-selected .mbsc-calendar-cell-text.mbsc-calendar-today::after {
                content: "";
                position: absolute;
                /* The border draws inward from the inset, so the visible gap is inset minus border
                   width: -6px with a 2px ring gives a 4px gap. Both numbers matter — a thicker ring
                   at a smaller inset just merges into the fill, since ring and fill are the same
                   colour and only the gap separates them. 44px outer is about the ceiling; rows are
                   flex-sized and a taller ring starts crowding the days above and below. */
                inset: -6px;
                border: 2px solid ${accent};
                border-radius: 50%;
                pointer-events: none;
            }
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-range-day::after {
                background-color: ${alpha(accent, 0.16)} !important;
            }
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-range-control-value.active,
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-range-control-label.active {
                color: ${accent} !important;
            }
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-material.mbsc-range-control-wrapper .mbsc-material.mbsc-segmented-button.mbsc-selected {
                border-bottom-color: ${accent} !important;
                color: ${accent} !important;
            }
            /* mobiscroll's per-endpoint clear button: our DateRange has no empty state, and it sits
               in the header corner where it reads as "close the popup" */
            .${NEW_DATE_PICKER_POPUP_CLASS} .mbsc-range-label-clear {
                display: none !important;
            }
        `;
    }, [accent, accentContrast, fontFamily]);

    return accent;
};

/** True on narrow screens and short-side phone landscape. */
export const useIsMobile = usePhoneLayout;

/**
 * Whether the prev/next step buttons should sit inside the field. "inside"/"outside" force it;
 * "auto" tucks them inside below the sm breakpoint (mobile) and flanks the field above it.
 */
export const useStepButtonsInside = (
    placement: StepButtonsPlacement,
    showStepButtons: boolean,
): boolean => {
    const isMobile = useIsMobile();
    return showStepButtons && (placement === "inside" || (placement === "auto" && isMobile));
};

/** Coerces a Mobiscroll onChange value (Date | string | number) into a start-of-day Date. */
export const toPickerDate = (value: unknown): Date | undefined => {
    const date =
        value instanceof Date
            ? value
            : typeof value === "string" || typeof value === "number"
                ? new Date(value)
                : undefined;

    return date && !Number.isNaN(date.getTime()) ? startOfDay(date) : undefined;
};

/** Two-letter, locale-aware weekday prefix (e.g. "Tu", "Di"). */
export const weekdayLabel = (language: string, date: Date): string =>
    new Intl.DateTimeFormat(language, {weekday: "short"}).format(date).replace(".", "").slice(0, 2);

/** Free-standing step button (desktop / "outside" placement). */
export const stepButtonSx = (size: DatePickerSize): SxProps<Theme> => ({
    width: DATE_PICKER_SIZE_TOKENS[size].stepOutside,
    height: DATE_PICKER_SIZE_TOKENS[size].stepOutside,
    // rounder on desktop, matching the trigger shell; tighter on mobile where the controls are smaller
    borderRadius: {xs: 1, md: 2},
    color: "text.secondary",
    "&:hover": {
        backgroundColor: "action.hover",
        color: "text.primary",
    },
});

/** Step button when tucked inside the field (mobile / "inside" placement): full-height, flush. */
export const insideStepButtonSx = (size: DatePickerSize): SxProps<Theme> => ({
    width: DATE_PICKER_SIZE_TOKENS[size].stepInsideWidth,
    height: "100%",
    borderRadius: 0,
    flexShrink: 0,
    color: "text.secondary",
    "&:hover": {
        backgroundColor: "action.hover",
        color: "text.primary",
    },
});

/** Merges an optional sx into an array form for spreading. */
export const mergeSx = (sx?: SxProps<Theme>) => (Array.isArray(sx) ? sx : sx ? [sx] : []);

/** Border styling for the trigger shell — disabled vs interactive (with a no-shift focus ring). */
export const triggerStateSx = (disabled: boolean, accent: string): SxProps<Theme> =>
    disabled
        ? {
            borderColor: "action.disabled",
            backgroundColor: "action.disabledBackground",
            boxShadow: "none",
            color: "text.disabled",
            cursor: "not-allowed",
            "&:hover": {
                borderColor: "action.disabled",
                backgroundColor: "action.disabledBackground",
            },
        }
        : {
            cursor: "default",
            "&:hover": {
                borderColor: "text.primary",
            },
            // Recolour + visually thicken the border on focus WITHOUT changing its width. A real
            // width change (1px -> 2px) reflows the inner content by 1px under box-sizing:
            // border-box; an inset shadow paints the extra thickness with zero layout impact.
            "&:focus-within": {
                borderColor: "primary.main",
                boxShadow: `inset 0 0 0 1px ${accent}`,
            },
        };
